import {
  AttachmentBox_Constraint_,
  AttachmentBox_Update_Column_,
  EsrsAssessmentDocument_,
  EsrsAssessmentMaterialStandardsDocument_,
  Esrs_MaterialMetricTag_Constraint_,
  Esrs_MaterialMetricTag_Update_Column_,
  Esrs_MaterialMetric_Constraint_,
  Esrs_MaterialMetric_Update_Column_,
  Esrs_MaterialTagValue_Constraint_,
  Esrs_MaterialTagValue_Update_Column_,
  GetCategoryAndStandardsDocument_,
  GetMaterialStandardDocument_,
  GetMaterialStandardQuery_,
  GetMaterialStandardsIdsDocument_,
  GetParentMaterialityAssessmentQuery_,
  GetRequiredTagsMaterialMetricsDocument_,
  GetStandardMandatoryMetricsQuery_,
  GetStandardNonMandatoryMetricsDocument_,
  GetSubsidiaryRequiredTagsMaterialMetricsDocument_,
  NoteHistory_Constraint_,
  NoteHistory_Update_Column_,
  useGetEsrsStandardQuery,
  useGetMaterialStandardQuery,
  useGetParentMaterialityAssessmentQuery,
  useUpdateSubsidiariesMaterialityMutation,
  useUpsertMaterialStandardMutation,
  useUpsertPolicyMutation,
} from 'models';
import {
  MaterialityState,
  ParentMaterialMetricsType,
  ParentMaterialityState,
  ParentStandardMaterialityData,
  StandardMaterialityState,
} from './DoubleMaterialityAssessment.d';
import { useParams } from 'react-router-dom';
import { useCompanyType, useToast } from 'utils/hooks';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useGetSubsidiaryType } from 'containers/Esrs/EsrsAssessment.hooks';
import { DataCollectionLevel } from '../../DataCollection';
import {
  DisclosureRequirementGroups,
  MaterialMetric,
  Requirement,
} from '../MaterialityAssessment.d';
import { isParentMaterial } from '../MaterialityAssessment.hooks';

export const getStandardMateriality = (
  isCollectOnlyMandatory: boolean,
  isCollectData: boolean,
  isCollectOnly: boolean,
  isStandardMandatory: boolean,
  materialStandard?: GetMaterialStandardQuery_['materialityAssessment'][number]
) => {
  if (!materialStandard && !isStandardMandatory && !isCollectOnlyMandatory)
    return StandardMaterialityState.toAssess;
  if (isStandardMandatory && !isCollectOnly) {
    return StandardMaterialityState.mandatory;
  } else if (isCollectOnlyMandatory) {
    return StandardMaterialityState.collectDataMandatory;
  } else if (isCollectData) {
    return StandardMaterialityState.collectData;
  } else if (!materialStandard?.isMaterial && isCollectOnly) {
    return StandardMaterialityState.doNotCollect;
  } else if (materialStandard?.isPhasedIn) {
    return StandardMaterialityState.phasedIn;
  } else if (materialStandard?.isDataGatheringOnly) {
    return StandardMaterialityState.gatherData;
  } else if (materialStandard?.isMaterial) {
    return StandardMaterialityState.material;
  } else if (materialStandard?.isMaterial === null) {
    return StandardMaterialityState.toAssess;
  } else if (!materialStandard?.isMaterial) {
    return StandardMaterialityState.notMaterial;
  }
  return StandardMaterialityState.toAssess;
};

export const getParentStandardMateriality = (
  isAssessed: boolean,
  isMaterial?: boolean | null,
  isPhasedIn?: boolean
) => {
  if (isPhasedIn) return ParentMaterialityState.phasedIn;
  if (isMaterial) return ParentMaterialityState.material;
  else if (isAssessed) return ParentMaterialityState.notMaterial;
  return ParentMaterialityState.toAssess;
};

export const useGetStandardMaterialityData = () => {
  const { standardRef = '', esrsAssessmentId = '' } = useParams();
  const { companyType } = useCompanyType();

  const { data: standardData, loading: standardDataLoading } = useGetEsrsStandardQuery({
    variables: { reference: standardRef },
    skip: !standardRef,
  });

  const { data: materialStandardData, loading: materialStandardLoading } =
    useGetMaterialStandardQuery({
      variables: { standardRef: standardRef, assessmentId: esrsAssessmentId },
      skip: !standardRef || !esrsAssessmentId,
    });

  const { data: parentData, loading: parentDataLoading } = useGetParentMaterialityAssessmentQuery({
    variables: {
      childAssessmentId: esrsAssessmentId,
    },
    skip: !esrsAssessmentId,
  });

  const standard = useMemo(() => standardData?.esrsStandard, [standardData]);
  const materialStandard = useMemo(
    () => materialStandardData?.materialityAssessment[0],
    [materialStandardData]
  );
  const parentStandardMaterialityData = useMemo(
    () =>
      parentData?.EsrsAssessment_by_pk?.parentAssessment?.materialStandards.find(
        (mA) => mA.standardRef === standardRef
      ),
    [parentData, standardRef]
  );

  const { isCollectOnly, subsidiaryTypeLoading } = useGetSubsidiaryType(esrsAssessmentId);

  const hasParent = useMemo(
    () => parentData?.EsrsAssessment_by_pk?.parentAssessment !== null,
    [parentData]
  );
  const isGroupOwner = useMemo(() => companyType === 'group-company', [companyType]);

  return {
    standard,
    materialStandard,
    parentStandardMaterialityData,
    hasParent,
    isGroupOwner,
    parentData,
    isCollectOnly,
    loading:
      standardDataLoading || materialStandardLoading || parentDataLoading || subsidiaryTypeLoading,
  };
};

export const mapMaterialTags = (
  metric: MaterialMetric['metric'] | GetStandardMandatoryMetricsQuery_['EsrsMetric'][number]
) => {
  const hasTags = !!metric?.tags?.length;
  const parentHasTags = metric?.parentMetrics?.some((met) =>
    met.parentMetric?.tags?.some((tag) => tag.tagType.alwaysAll && !tag.isOptional)
  );
  const metricTags =
    metric?.tags?.filter((tag) => tag.tagType?.alwaysAll && !tag?.isOptional) ?? [];

  return hasTags
    ? metricTags?.map((tag) => ({
        tagType: tag.tagType.type,
        materialTagValues: {
          data: tag.tagType.tagValues.map((val) => ({
            tagType: tag.tagType.type,
            tagValue: val.value,
          })),
          on_conflict: {
            constraint:
              Esrs_MaterialTagValue_Constraint_.MaterialTagValueMaterialMetricTagIdTagValueTagTypeKey_,
            update_columns: [Esrs_MaterialTagValue_Update_Column_.TagValue_],
          },
        },
      }))
    : parentHasTags
      ? (metric?.parentMetrics.flatMap((met) =>
          met.parentMetric.tags
            ?.filter((tag) => tag.tagType.alwaysAll)
            .map((tag) => ({
              tagType: tag.tagType.type,
              materialTagValues: {
                data: tag.tagType.tagValues.map((val) => ({
                  tagType: tag.tagType.type,
                  tagValue: val.value,
                })),
                on_conflict: {
                  constraint:
                    Esrs_MaterialTagValue_Constraint_.MaterialTagValueMaterialMetricTagIdTagValueTagTypeKey_,
                  update_columns: [Esrs_MaterialTagValue_Update_Column_.TagValue_],
                },
              },
            }))
        ) ?? [])
      : [];
};

const checkEntityType = (metric: GetStandardMandatoryMetricsQuery_['EsrsMetric'][0]) => {
  return metric.additionalTypes.some((additionalType) =>
    additionalType.additionalTypeRef.includes('Entity')
  );
};

const getValuesBasedOnMateriality = (
  currentMateriality: StandardMaterialityState,
  metric: GetStandardMandatoryMetricsQuery_['EsrsMetric'][0]['materialMetrics'][0]
) => {
  if (metric.isMaterial === null || currentMateriality === StandardMaterialityState.toAssess) {
    return {
      isMaterial: null,
      isDataGatheringOnly: metric.isDataGatheringOnly,
      isPhasedIn: metric.isPhasedIn,
    };
  }
  if (currentMateriality === StandardMaterialityState.gatherData) {
    if (metric.isMaterial === true) {
      return {
        isMaterial: true,
        isDataGatheringOnly: true,
        isPhasedIn: false,
      };
    }
    return {
      isMaterial: metric.isMaterial,
      isDataGatheringOnly: metric.isDataGatheringOnly,
      isPhasedIn: metric.isPhasedIn,
    };
  }
  if (currentMateriality === StandardMaterialityState.notMaterial) {
    return {
      isMaterial: false,
      isDataGatheringOnly: false,
      isPhasedIn: false,
    };
  }
  if (currentMateriality === StandardMaterialityState.material) {
    return {
      isMaterial: metric.isMaterial,
      isDataGatheringOnly: metric.isDataGatheringOnly,
      isPhasedIn: metric.isPhasedIn,
    };
  }
  if (currentMateriality === StandardMaterialityState.phasedIn) {
    if (
      (metric.isMaterial === true &&
        metric.isDataGatheringOnly === true &&
        metric.isPhasedIn === false) ||
      metric.isMaterial === false
    ) {
      return {
        isMaterial: metric.isMaterial,
        isDataGatheringOnly: metric.isDataGatheringOnly,
        isPhasedIn: metric.isPhasedIn,
      };
    }
    return {
      isMaterial: true,
      isDataGatheringOnly: true,
      isPhasedIn: true,
    };
  }
};

const mapMandatoryMetrics = (
  mandatoryMetrics: GetStandardMandatoryMetricsQuery_['EsrsMetric'],
  isMaterial: boolean | null | undefined,
  isDataGatheringOnly: boolean | undefined,
  isGroup: boolean,
  parentStandardMaterialityData?: ParentStandardMaterialityData,
  isCollectOnly?: boolean,
  isPhasedIn?: boolean,
  materialStandardId?: string,
  currentMateriality?: StandardMaterialityState
) => {
  const metricMap = new Map();
  const defaultDataCollection = isGroup
    ? DataCollectionLevel.subsidiaries
    : DataCollectionLevel.company;

  mandatoryMetrics
    .filter((m) => {
      if (isCollectOnly) {
        return parentStandardMaterialityData?.materialMetrics.some(
          (met) =>
            met.metricRef === m.reference &&
            met.isMaterial &&
            met.dataCollection === DataCollectionLevel.subsidiaries &&
            met.metric.childrenMetricsCount === 0
        );
      }
      return true;
    })
    .forEach((m) => {
      if (!m.requirement.isMandatory && currentMateriality) {
        const metric = m.materialMetrics.find((mm) => mm.materialStandardId === materialStandardId);
        if (metric) {
          const metricMateriality = getValuesBasedOnMateriality(currentMateriality, metric);
          isMaterial = metricMateriality?.isMaterial;
          isDataGatheringOnly = metricMateriality?.isDataGatheringOnly;
          isPhasedIn = metricMateriality?.isPhasedIn;
        } else {
          isMaterial = null;
          isDataGatheringOnly = false;
          isPhasedIn = false;
        }
      }
      const isEntity = checkEntityType(m);
      metricMap.set(m.reference, {
        isMaterial: isEntity ? null : isMaterial,
        isDataGatheringOnly: isEntity ? false : isDataGatheringOnly,
        isPhasedIn: isEntity ? false : isPhasedIn,
        metricRef: m.reference,
        dataCollection: defaultDataCollection,
        materialMetricTags: {
          data: mapMaterialTags(m),
          on_conflict: {
            constraint:
              Esrs_MaterialMetricTag_Constraint_.MaterialMetricTagMaterialMetricIdTagTypeKey_,
            update_columns: [Esrs_MaterialMetricTag_Update_Column_.TagType_],
          },
        },
      });
    });

  return Array.from(metricMap.values());
};

export const useAddMateriality = (setIsLoading?: Dispatch<SetStateAction<boolean>>) => {
  const { standardRef, esrsAssessmentId } = useParams();
  const [upsertMaterialStandard] = useUpsertMaterialStandardMutation();
  const [updateSubsidiariesMateriality] = useUpdateSubsidiariesMaterialityMutation();
  const [upsertPolicy] = useUpsertPolicyMutation();
  const toast = useToast();
  return useCallback(
    (
      material: StandardMaterialityState,
      mandatoryMetrics: GetStandardMandatoryMetricsQuery_['EsrsMetric'],
      isGroup: boolean,
      parentStandardMaterialityData?: ParentStandardMaterialityData,
      isCollectOnly?: boolean,
      materialStandardId?: string
    ) => {
      const isMaterial =
        material === StandardMaterialityState.toAssess
          ? null
          : material === StandardMaterialityState.material ||
            material === StandardMaterialityState.gatherData ||
            material === StandardMaterialityState.collectData ||
            material === StandardMaterialityState.collectDataMandatory ||
            material === StandardMaterialityState.phasedIn;
      const isDataGatheringOnly =
        material === StandardMaterialityState.gatherData ||
        material === StandardMaterialityState.phasedIn;
      const isPhasedIn = material === StandardMaterialityState.phasedIn;
      if (setIsLoading) {
        setIsLoading(true);
      }
      upsertMaterialStandard({
        variables: {
          materialStandard: {
            assessmentId: esrsAssessmentId,
            standardRef,
            isDataGatheringOnly,
            isMaterial,
            isPhasedIn,
            materialMetrics: {
              data: mapMandatoryMetrics(
                mandatoryMetrics,
                isMaterial,
                isDataGatheringOnly,
                isGroup,
                parentStandardMaterialityData,
                isCollectOnly,
                isPhasedIn,
                materialStandardId,
                material
              ),
              on_conflict: {
                constraint:
                  Esrs_MaterialMetric_Constraint_.MaterialMetricMaterialStandardIdMetricRefKey_,
                update_columns: [
                  Esrs_MaterialMetric_Update_Column_.IsMaterial_,
                  Esrs_MaterialMetric_Update_Column_.IsDataGatheringOnly_,
                  Esrs_MaterialMetric_Update_Column_.IsPhasedIn_,
                ],
              },
            },
            attachmentBox: {
              data: {},
              on_conflict: {
                constraint: AttachmentBox_Constraint_.AttachmentBoxMaterialityAssessmentIdKey_,
                update_columns: [AttachmentBox_Update_Column_.MaterialStandardId_],
              },
            },
            noteHistory: {
              data: {},
              on_conflict: {
                constraint: NoteHistory_Constraint_.NoteHistoryMaterialityAssessmentIdKey_,
                update_columns: [NoteHistory_Update_Column_.MaterialStandardId_],
              },
            },
          },
        },
        refetchQueries: [
          GetMaterialStandardDocument_,
          GetRequiredTagsMaterialMetricsDocument_,
          GetStandardNonMandatoryMetricsDocument_,
          GetSubsidiaryRequiredTagsMaterialMetricsDocument_,
          EsrsAssessmentDocument_,
          GetCategoryAndStandardsDocument_,
          GetMaterialStandardsIdsDocument_,
          EsrsAssessmentMaterialStandardsDocument_,
        ],
        awaitRefetchQueries: true,
      })
        .then((res) => {
          if (setIsLoading) {
            setIsLoading(false);
          }
          toast({
            text: material ? 'Materiality assessment updated!' : 'Materiality assessment added!',
          });
          const result = res.data?.insert_esrs_MaterialStandard_one;
          if (isMaterial && isGroup) {
            updateSubsidiariesMateriality({
              variables: { id: res.data?.insert_esrs_MaterialStandard_one?.id },
            });
          }
          if (isMaterial !== false) {
            const requirementsWithPolicies = result?.standard.disclosureRequirementGroups.filter(
              (g) => g.requirements.filter((r) => r.type === 'policy').length
            );
            upsertPolicy({
              variables: {
                input:
                  requirementsWithPolicies?.map((group) => {
                    return {
                      assessmentId: result?.id,
                      disclosureRequirementRef: group.requirements[0].reference,
                      noteHistory: {
                        data: {},
                        on_conflict: {
                          constraint: NoteHistory_Constraint_.NoteHistoryPolicyIdKey_,
                          update_columns: [],
                        },
                      },
                      attachmentBox: {
                        data: {},
                        on_conflict: {
                          constraint: AttachmentBox_Constraint_.AttachmentBoxPoliciesIdKey_,
                          update_columns: [],
                        },
                      },
                    };
                  }) ?? [],
              },
            });
          }
        })
        .catch(() => {
          if (setIsLoading) {
            setIsLoading(false);
          }
          toast({ text: 'Failed to add materiality assessment', variant: 'danger' });
        });
    },
    [upsertMaterialStandard, upsertPolicy]
  );
};

const useGetDRState = () => {
  return useCallback(
    (
      requirement: Requirement,
      materialMetrics: MaterialMetric[],
      parentMaterialMetrics: ParentMaterialMetricsType,
      isStandardMandatory: boolean,
      isStandardMaterial: boolean,
      isCollectOnly: boolean,
      isParentStandardMaterial: boolean
    ): MaterialityState => {
      const metrics = materialMetrics?.filter(
        (metric) =>
          metric.metric?.requirement?.reference === requirement?.reference &&
          metric.metric.childrenMetricsCount === 0
      );

      const parentMetrics = parentMaterialMetrics?.filter(
        (metric) => metric.metric?.disclosureRequirementRef === requirement?.reference
      );

      if (isCollectOnly) {
        if (
          isParentMaterial(parentMetrics.filter((m) => m.metric.childrenMetricsCount === 0)) &&
          isParentStandardMaterial
        ) {
          return MaterialityState.collectDataMandatory;
        }
        if (
          metrics?.some(
            (metric) => metric.isMaterial && metric.dataCollection !== DataCollectionLevel.group
          )
        ) {
          return MaterialityState.collectData;
        } else {
          return MaterialityState.doNotCollect;
        }
      }
      if (isStandardMandatory) {
        return MaterialityState.mandatory;
      }
      if (requirement.isMandatory && isStandardMaterial) {
        return MaterialityState.materialMandatory;
      }

      const isDRAssessed = !!metrics.length && metrics.some((m) => m.isMaterial !== null);
      if (!isDRAssessed) return MaterialityState.toAssess;

      const isPhasedIn =
        requirement.additionalTypes.some((additionalType) =>
          additionalType?.additionalType?.title?.includes('Phased')
        ) && metrics.some((m) => m.isMaterial && m.isPhasedIn && m.isDataGatheringOnly);

      if (isPhasedIn) return MaterialityState.phasedIn;

      const isMaterial = metrics.some((m) => m.isMaterial && !m.isDataGatheringOnly);

      if (isMaterial) return MaterialityState.material;

      const isDataGathering = metrics.some((m) => m.isDataGatheringOnly && m.isMaterial);
      if (isDataGathering) return MaterialityState.gatherData;

      return MaterialityState.notMaterial;
    },
    []
  );
};

const useGetParentDRstate = () => {
  return useCallback(
    (
      requirement: Requirement,
      parentMaterialMetrics: NonNullable<
        NonNullable<
          GetParentMaterialityAssessmentQuery_['EsrsAssessment_by_pk']
        >['parentAssessment']
      >['materialStandards'][number]['materialMetrics'],
      isStandardMaterial: boolean
    ): MaterialityState => {
      const metrics = parentMaterialMetrics.filter(
        (m) => m.metric?.disclosureRequirementRef === requirement?.reference
      );
      if (!isStandardMaterial) {
        return MaterialityState.notMaterial;
      }
      const isPhasedIn = metrics.some((m) => m.isMaterial && m.isDataGatheringOnly && m.isPhasedIn);
      if (isPhasedIn) return MaterialityState.phasedIn;
      if (requirement?.isMandatory) {
        return MaterialityState.mandatory;
      }

      const isDRAssessed = !!metrics.length && metrics.some((m) => m.isMaterial !== null);

      if (!isDRAssessed) return MaterialityState.toAssess;

      const isMaterial = metrics.some((m) => m.isMaterial && !m.isDataGatheringOnly);

      if (isMaterial) return MaterialityState.material;

      const isDataGathering = metrics.some((m) => m.isDataGatheringOnly && m.isMaterial);
      if (isDataGathering) return MaterialityState.gatherData;

      return MaterialityState.notMaterial;
    },
    []
  );
};

const getMetricTags = (metric?: MaterialMetric) => {
  const tags = !!metric?.materialMetricTags?.length
    ? metric?.materialMetricTags?.map((tag) => ({
        type: tag.tagType,
        isOptional: tag.isOptional,
        tagType: {
          type: tag.tagType,
          alwaysAll: tag.metricTagType.alwaysAll,
          tagValues: tag.materialTagValues.map((val) => ({
            value: val.tagValue,
          })),
        },
      }))
    : metric?.metric?.tags;
  return tags;
};

export const useMapDisclosureRequirements = () => {
  const getDrState = useGetDRState();
  const getParentDRState = useGetParentDRstate();
  return useCallback(
    ({
      materialMetrics,
      parentMaterialMetrics,
      isGroupOwner,
      isStandardMandatory,
      isStandardMaterial,
      isCollectOnly,
      requirementGroups,
      isParentStandardMaterial,
    }: {
      materialMetrics: MaterialMetric[];
      parentMaterialMetrics: ParentMaterialMetricsType;
      isGroupOwner: boolean;
      isStandardMandatory: boolean;
      isStandardMaterial: boolean;
      isCollectOnly: boolean;
      requirementGroups?: DisclosureRequirementGroups;
      isParentStandardMaterial?: boolean | null;
    }) => {
      const allRequirements = Object.values(requirementGroups ?? {}).flatMap(
        (group) => group.requirements
      );

      const requirements = allRequirements.slice().sort((a, b) => {
        return a.order - b.order || a.reference.localeCompare(b.reference);
      });

      const mappedRequirements = requirements.map((req) => ({
        title: req.title,
        drRef: req.reference,
        isMandatory: req.isMandatory,
        materialityStatus: getDrState(
          req,
          materialMetrics,
          parentMaterialMetrics,
          isStandardMandatory,
          isStandardMaterial,
          isCollectOnly,
          isParentStandardMaterial ?? false
        ),
        order: req.order,
        parentMateriality: !isGroupOwner
          ? getParentDRState(req, parentMaterialMetrics, isParentStandardMaterial ?? false)
          : MaterialityState.toAssess,
        metrics: req.metrics.map((metric) => ({
          ...metric,
          tags:
            getMetricTags((materialMetrics ?? []).find((m) => m.metricRef === metric.reference)) ??
            metric.tags,
        })),
        description: req.description,
        additionalTypes: req.additionalTypes.map((at) => at.additionalType),
      }));
      return mappedRequirements;
    },
    []
  );
};

export const useMapDisclosureRequirement = () => {
  const getDrState = useGetDRState();
  const getParentDRState = useGetParentDRstate();
  return useCallback(
    ({
      materialMetrics,
      parentMaterialMetrics,
      isGroupOwner,
      isStandardMandatory,
      isStandardMaterial,
      isCollectOnly,
      drGroups,
      isParentStandardMaterial,
      drRef,
    }: {
      materialMetrics: MaterialMetric[];
      parentMaterialMetrics: ParentMaterialMetricsType;
      isGroupOwner: boolean;
      isStandardMandatory: boolean;
      isStandardMaterial: boolean;
      isCollectOnly: boolean;
      drGroups?: DisclosureRequirementGroups;
      isParentStandardMaterial?: boolean | null;
      drRef: string;
    }) => {
      const req = drGroups
        ?.find((grp) => grp.requirements.some((req) => req.reference === drRef))
        ?.requirements.find((req) => req.reference === drRef);
      return {
        title: req?.title ?? '',
        drRef: req?.reference ?? '',
        isMandatory: req?.isMandatory ?? false,
        materialityStatus: getDrState(
          req!,
          materialMetrics,
          parentMaterialMetrics,
          isStandardMandatory,
          isStandardMaterial,
          isCollectOnly,
          isParentStandardMaterial ?? false
        ),
        order: req?.order ?? 0,
        mfDisclosureRequirementRefs: req?.mfDisclosureRequirements.map(
          (mfDR) => mfDR.mfDisclosureRequirementRef
        ),
        parentMateriality: !isGroupOwner
          ? getParentDRState(req!, parentMaterialMetrics, isParentStandardMaterial ?? false)
          : MaterialityState.toAssess,
        metrics:
          req?.metrics.map((metric) => {
            return {
              ...metric,
              tags:
                getMetricTags(
                  (materialMetrics ?? []).find((m) => m.metricRef === metric.reference)
                ) ?? metric.tags,
            };
          }) ?? [],
        description: req?.description,
        additionalTypes: req?.additionalTypes.map((at) => at.additionalType) ?? [],
      };
    },
    []
  );
};
